import { useState, useRef, useEffect, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import * as S from './styles';

export type LabelPosition = 'top' | 'left';

export interface DateInputProps {
  initialValue?: string;
  setValue: (data: string) => void;
  onBlur?: () => void;
  id: string;
  hasLabel?: boolean;
  label?: string;
  required?: boolean;
  width?: string;
  labelPosition?: LabelPosition;
}

export function DateInput({
  initialValue,
  setValue,
  onBlur,
  id,
  hasLabel = false,
  label,
  required = false,
  width,
  labelPosition = 'top',
}: DateInputProps) {
  const [displayValue, setDisplayValue] = useState(
    initialValue?.split('-').reverse().join('/') || ''
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialValue ? new Date(initialValue + 'T00:00:00') : null
  );
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (initialValue) {
      setDisplayValue(initialValue?.split('-').reverse().join('/') || '');
      setSelectedDate(initialValue ? new Date(initialValue + 'T00:00:00') : null);
    } else {
      setDisplayValue('');
      setSelectedDate(null);
    }
  }, [initialValue]);

  const handleBlur = () => {
    if (!displayValue) {
      setDisplayValue('');
      setValue('');
      setSelectedDate(null);
      onBlur?.();
      return;
    }

    let isSetDate = false;
    let date = new Date();
    if (displayValue.toLowerCase() === 'h') {
      isSetDate = true;
    }

    if (displayValue.toLowerCase() === 'o') {
      date.setDate(date.getDate() - 1);
      isSetDate = true;
    }

    if (/^-?\d+$/.test(displayValue)) {
      const days = parseInt(displayValue);
      date.setDate(date.getDate() + days);
      isSetDate = true;
    }

    if (/^\d{1,2}\/\d{1,2}$/.test(displayValue)) {
      const [day, month] = displayValue.split('/');
      date.setDate(parseInt(day));
      date.setMonth(parseInt(month) - 1);
      isSetDate = true;
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{1,2}$/.test(displayValue)) {
      const [day, month, year] = displayValue.split('/');
      date = new Date(
        2000 + parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      isSetDate = true;
    }

    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(displayValue)) {
      const [day, month, year] = displayValue.split('/');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      isSetDate = true;
    }

    if (isSetDate) {
      setDisplayValue(
        date.getDate().toString().padStart(2, '0') +
          '/' +
          (date.getMonth() + 1).toString().padStart(2, '0') +
          '/' +
          date.getFullYear()
      );
      setValue(
        date.getFullYear() +
          '-' +
          (date.getMonth() + 1).toString().padStart(2, '0') +
          '-' +
          date.getDate().toString().padStart(2, '0')
      );
      setSelectedDate(date);
      setCurrentDate(date);
    } else {
      setDisplayValue('');
      setValue('');
      setSelectedDate(null);
    }
    onBlur?.();
  };

  const getWeekNumber = (date: Date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear =
      (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1)
    );
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(newDate);
    setDisplayValue(
      newDate.getDate().toString().padStart(2, '0') +
        '/' +
        (newDate.getMonth() + 1).toString().padStart(2, '0') +
        '/' +
        newDate.getFullYear()
    );
    setValue(
      newDate.getFullYear() +
        '-' +
        (newDate.getMonth() + 1).toString().padStart(2, '0') +
        '-' +
        newDate.getDate().toString().padStart(2, '0')
    );
    setShowCalendar(false);
    onBlur?.();
  };

  const handleToday = () => {
    const today = new Date();
    handleDateSelect(today.getDate());
  };

  const handleClear = () => {
    setDisplayValue('');
    setValue('');
    setSelectedDate(null);
    setShowCalendar(false);
    onBlur?.();
  };

  const renderCalendar = () => {
    const daysInMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    ).getDate();
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).getDay();
    const days: ReactNode[] = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<S.CalendarCell key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const isSelected =
        selectedDate !== null &&
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear();
      const isToday = date.toDateString() === new Date().toDateString();

      days.push(
        <S.CalendarCell
          key={day}
          isSelected={isSelected}
          isToday={isToday}
          onClick={() => handleDateSelect(day)}
        >
          {day}
        </S.CalendarCell>
      );
    }

    return days;
  };

  return (
    <S.InputContainer width={width} labelPosition={labelPosition}>
      {hasLabel && label && (
        <S.InputLabel htmlFor={id} required={required} labelPosition={labelPosition}>
          {label}
        </S.InputLabel>
      )}
      <div style={{ position: 'relative', flex: labelPosition === 'left' ? 1 : 'initial' }}>
        <S.InputGroup>
          <S.Input
            type="text"
            value={displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            onBlur={handleBlur}
            placeholder="DD/MM/AAAA"
            id={id}
            onFocus={(e) => e.target.select()}
          />
          <S.CalendarButton
            type="button"
            onClick={() => setShowCalendar(!showCalendar)}
          >
            <FontAwesomeIcon icon={faCalendar} />
          </S.CalendarButton>
        </S.InputGroup>

        {showCalendar && (
          <S.CalendarContainer ref={calendarRef}>
            <S.CalendarHeader>
              <S.NavigationButton onClick={handlePrevMonth}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </S.NavigationButton>
              <S.MonthTitle>
                {
                  [
                    'Jan',
                    'Fev',
                    'Mar',
                    'Abr',
                    'Mai',
                    'Jun',
                    'Jul',
                    'Ago',
                    'Set',
                    'Out',
                    'Nov',
                    'Dez',
                  ][currentDate.getMonth()]
                } {currentDate.getFullYear()}
              </S.MonthTitle>
              <S.NavigationButton onClick={handleNextMonth}>
                <FontAwesomeIcon icon={faChevronRight} />
              </S.NavigationButton>
            </S.CalendarHeader>

            <div style={{ padding: '0.5rem' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: '4px',
                }}
              >
                {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(
                  (day, index) => (
                    <div
                      key={index}
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        padding: '0.5rem 0',
                        fontSize: '0.75rem',
                      }}
                    >
                      {day}
                    </div>
                  )
                )}
                {renderCalendar()}
              </div>
            </div>

            <S.CalendarFooter>
              <S.ButtonGroup>
                <S.ActionButton
                  type="button"
                  onClick={handleToday}
                  variant="primary"
                >
                  Hoje
                </S.ActionButton>
                <S.ActionButton
                  type="button"
                  onClick={handleClear}
                  variant="danger"
                >
                  Limpar
                </S.ActionButton>
              </S.ButtonGroup>
            </S.CalendarFooter>
          </S.CalendarContainer>
        )}
      </div>
    </S.InputContainer>
  );
}
